/**
 * breathingFavicon.js
 *
 * Animates the browser tab favicon with the same "lively" breathing pulse
 * we use on the V4.5 flame mark elsewhere in the app. Works in Chrome,
 * Firefox, Edge (animated SVG favicons are flaky in Safari, so we use the
 * canvas approach — universal coverage).
 *
 * Strategy:
 *   1. Load the V4.5 PNG once into a hidden 64×64 canvas.
 *   2. Each animation tick (capped to ~10fps) draw the PNG at a varying
 *      scale + brightness/saturation filter to mimic the same CSS keyframes
 *      used by <BreathingFlameMark>.
 *   3. Export the canvas to a data URL and set it as the favicon href.
 *   4. Pause when the tab is hidden (no point burning CPU off-screen).
 *   5. Bail out entirely if the user has prefers-reduced-motion set.
 *
 * Period & amplitude mirror the "lively" CSS preset: 2.6s, ±6% scale,
 * brightness 0.87 → 1.13, saturation ±10%.
 */

const SIZE = 64;
const FPS = 10;
const PERIOD_MS = 2600;

let canvas = null;
let ctx = null;
let img = null;
let link = null;
let raf = 0;
let lastUpdate = 0;
let started = false;

const ensureFaviconLink = () => {
  // Prefer the largest declared icon link so we replace something that's
  // actually being used. Otherwise create one.
  const candidates = Array.from(
    document.querySelectorAll("link[rel~='icon']")
  );
  if (candidates.length) {
    // Use the first one; we'll also update siblings to keep them in sync.
    return candidates;
  }
  const created = document.createElement('link');
  created.rel = 'icon';
  document.head.appendChild(created);
  return [created];
};

const handleVisibility = () => {
  if (document.visibilityState === 'visible') start();
  else stop();
};

const start = () => {
  if (raf || !ctx || !img.complete) return;
  const loop = (t) => {
    if (t - lastUpdate >= 1000 / FPS) {
      drawFrame(t);
      lastUpdate = t;
    }
    raf = requestAnimationFrame(loop);
  };
  raf = requestAnimationFrame(loop);
};

const stop = () => {
  if (raf) cancelAnimationFrame(raf);
  raf = 0;
};

const drawFrame = (t) => {
  // 0 → 1 phase across the breathing period
  const phase = (t % PERIOD_MS) / PERIOD_MS;
  const sin = Math.sin(phase * Math.PI * 2); // -1..1
  const scale = 1 + sin * 0.06;              // ±6%
  const brightness = 1 + sin * 0.13;         // 0.87..1.13
  const saturate = 1 + sin * 0.1;            // 0.90..1.10

  ctx.clearRect(0, 0, SIZE, SIZE);
  ctx.save();
  ctx.translate(SIZE / 2, SIZE / 2);
  ctx.scale(scale, scale);
  // ctx.filter is supported in Chrome/Edge/Firefox (not older Safari, but
  // Safari favicons fall back to static anyway).
  try {
    ctx.filter = `brightness(${brightness}) saturate(${saturate})`;
  } catch {
    /* ignore */
  }
  ctx.drawImage(img, -SIZE / 2, -SIZE / 2, SIZE, SIZE);
  ctx.restore();

  const dataUrl = canvas.toDataURL('image/png');
  link.forEach((l) => {
    l.href = dataUrl;
  });
};

export const initBreathingFavicon = () => {
  if (started) return;
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Respect reduced-motion preference — keep static favicon.
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
  } catch { /* ignore */ }

  // No-op inside the Capacitor native shell — those platforms don't show favicons.
  if (window.Capacitor?.isNativePlatform?.()) return;

  started = true;

  canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  ctx = canvas.getContext('2d');

  img = new Image();
  img.decoding = 'async';
  img.src = '/brand/enferno-flame-mark-v46.png';
  img.onload = () => {
    link = ensureFaviconLink();
    document.addEventListener('visibilitychange', handleVisibility);
    if (document.visibilityState === 'visible') start();
  };
  img.onerror = () => {
    // Bail silently — keep whatever static favicon is currently set.
    started = false;
  };
};
